import React, { useState } from 'react';
import axios from 'axios';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  FileText, 
  Calendar,
  Users,
  UsersRound,
  HandHeart,
  Briefcase,
  Loader2,
  Download,
  Search,
  AlertTriangle,
  ArrowLeft,
  Eye,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Roles that can access this report
const ALLOWED_ROLES = ['system_admin', 'executive', 'operations_manager'];

const AddictionsRecoveryReportPage = () => {
  const { user } = useAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // Drill-down state
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantNotes, setParticipantNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Check if user has access
  const hasAccess = ALLOWED_ROLES.includes(user?.role);

  const generateReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/addictions-recovery-program`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setReportData(response.data);
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString || dateString === 'Unknown') return 'Unknown';
    try {
      return formatDate(dateString) || dateString;
    } catch {
      return dateString;
    }
  };

  const handleCurrentMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  };

  const viewParticipantDetails = async (participant) => {
    setSelectedParticipant(participant);
    setDetailDialogOpen(true);
    setLoadingNotes(true);
    
    try {
      const response = await axios.get(`${API}/reports/addictions-recovery-program/participant-notes`, {
        params: {
          participant_id: participant.participant_id,
          start_date: startDate,
          end_date: endDate
        }
      });
      setParticipantNotes(response.data.notes || []);
    } catch (error) {
      toast.error('Failed to load participant notes');
      setParticipantNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const exportReport = () => {
    if (!reportData) return;

    // Create CSV content
    let csv = 'The HELP Center Addictions Recovery Program - Tennessee\n';
    csv += 'Addictions Recovery Program Report\n\n';
    csv += `Reporting Period: ${formatDate(reportData.date_range.start_date)} through ${formatDate(reportData.date_range.end_date)}\n`;
    csv += `Generated: ${formatDate(reportData.generated_at)}\n`;
    csv += `Generated By: ${reportData.generated_by}\n\n`;
    
    csv += 'Summary Metrics\n';
    csv += `Total Participants Served,${reportData.metrics.total_participants_served}\n`;
    csv += `Total Group Participants,${reportData.metrics.total_group_participants}\n`;
    csv += `Total Supportive Services Issued,${reportData.metrics.total_supportive_services}\n`;
    csv += `Total Case Management Sessions,${reportData.metrics.total_case_management_sessions}\n\n`;
    
    csv += 'Participant Service List\n';
    csv += 'Participant Name,Participant ID,Case Status,Group Sessions,Support Services,Case Management,Total Notes,First Service,Last Service\n';
    reportData.participant_service_list.forEach(item => {
      csv += `"${item.participant_name}","${item.participant_number || item.participant_id}","${item.case_status}",${item.group_session_count},${item.support_service_count},${item.case_management_count},${item.total_notes},"${item.first_service_date}","${item.last_service_date}"\n`;
    });

    // Add Case Note Detail section
    if (reportData.case_note_details && reportData.case_note_details.length > 0) {
      csv += '\nCase Note Detail\n';
      csv += 'Participant Name,Participant ID,Case Note Subject,Category,Service Date,Entered By\n';
      reportData.case_note_details.forEach(note => {
        csv += `"${note.participant_name}","${note.participant_number || note.participant_id}","${note.subject}","${note.category}","${note.service_date}","${note.entered_by}"\n`;
      });
    }

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `addictions-recovery-program-report-${startDate}-to-${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  if (!hasAccess) {
    return (
      <div className="p-6">
        <Card className="card-shadow max-w-lg mx-auto">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-destructive" />
              <h2 className="text-xl font-bold text-destructive mb-2">Access Denied</h2>
              <p className="text-muted-foreground">
                Only System Administrators, Executives, and Operations Managers can access this report.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link to="/reports">
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Addictions Recovery Program Report</h1>
            <p className="text-muted-foreground">
              Generate reports for Addictions Recovery Program - Tennessee services
            </p>
          </div>
        </div>
        {reportData && (
          <Button onClick={exportReport} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        )}
      </div>

      {/* Date Filters */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Report Date Range
          </CardTitle>
          <CardDescription>
            Select the date range for the report. Uses Case Note service date.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-48"
                data-testid="arp-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-48"
                data-testid="arp-end-date"
              />
            </div>
            <Button
              variant="outline"
              onClick={handleCurrentMonth}
              data-testid="arp-current-month-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Current Month
            </Button>
            <Button 
              onClick={generateReport} 
              disabled={loading || !startDate || !endDate}
              data-testid="arp-generate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {reportData && (
        <>
          {/* Report Header */}
          <Card className="card-shadow bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Heart className="w-6 h-6 text-teal-600" />
                  <h2 className="text-xl font-bold text-teal-800">The HELP Center</h2>
                </div>
                <h3 className="text-lg font-semibold text-teal-700">Addictions Recovery Program - Tennessee</h3>
                <p className="text-sm text-teal-600 mt-1">
                  Reporting Period: {formatDate(reportData.date_range.start_date)} through {formatDate(reportData.date_range.end_date)}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Generated: {formatDateTime(reportData.generated_at)} by {reportData.generated_by}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="card-shadow bg-gradient-to-br from-teal-50 to-white border-teal-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Participants Served</p>
                    <p className="text-3xl font-bold text-teal-700">
                      {reportData.metrics.total_participants_served}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Unique participants</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow bg-gradient-to-br from-blue-50 to-white border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <UsersRound className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Group Participants</p>
                    <p className="text-3xl font-bold text-blue-700">
                      {reportData.metrics.total_group_participants}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">With Group Session notes</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow bg-gradient-to-br from-purple-50 to-white border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <HandHeart className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Supportive Services</p>
                    <p className="text-3xl font-bold text-purple-700">
                      {reportData.metrics.total_supportive_services}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Service count</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow bg-gradient-to-br from-orange-50 to-white border-orange-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Case Management Sessions</p>
                    <p className="text-3xl font-bold text-orange-700">
                      {reportData.metrics.total_case_management_sessions}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Session count</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Participant Service List */}
          <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Participant Service List
              </CardTitle>
              <CardDescription>
                Service counts for each participant during {formatDate(reportData.date_range.start_date)} - {formatDate(reportData.date_range.end_date)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reportData.participant_service_list.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Participant Name</TableHead>
                        <TableHead>Participant ID</TableHead>
                        <TableHead>Case Status</TableHead>
                        <TableHead className="text-center">Group Sessions</TableHead>
                        <TableHead className="text-center">Support Services</TableHead>
                        <TableHead className="text-center">Case Management</TableHead>
                        <TableHead className="text-center">Total Notes</TableHead>
                        <TableHead>First Service</TableHead>
                        <TableHead>Last Service</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.participant_service_list.map((item) => (
                        <TableRow key={item.participant_id}>
                          <TableCell className="font-medium">{item.participant_name}</TableCell>
                          <TableCell className="font-mono text-sm">{item.participant_number || item.participant_id}</TableCell>
                          <TableCell>
                            <Badge variant={item.case_status === 'open' ? 'default' : 'secondary'}>
                              {item.case_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              {item.group_session_count}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-purple-50 text-purple-700">
                              {item.support_service_count}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-orange-50 text-orange-700">
                              {item.case_management_count}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center font-semibold">
                            {item.total_notes}
                          </TableCell>
                          <TableCell className="text-sm">{formatDate(item.first_service_date)}</TableCell>
                          <TableCell className="text-sm">{formatDate(item.last_service_date)}</TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => viewParticipantDetails(item)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No qualifying services found in the selected date range</p>
                  <p className="text-sm mt-1">
                    Services are counted from case notes in open Addictions Recovery Program - Tennessee cases
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Case Note Detail Section */}
          {reportData.case_note_details && reportData.case_note_details.length > 0 && (
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Case Note Detail
                </CardTitle>
                <CardDescription>
                  Individual Case Management, Group Session, and Supportive Service notes recorded during {formatDate(reportData.date_range.start_date)} - {formatDate(reportData.date_range.end_date)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Participant Name</TableHead>
                        <TableHead>Participant ID</TableHead>
                        <TableHead>Case Note Subject</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Service Date</TableHead>
                        <TableHead>Entered By</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.case_note_details.map((note, index) => (
                        <TableRow key={`${note.note_id}-${index}`}>
                          <TableCell className="font-medium">{note.participant_name}</TableCell>
                          <TableCell className="font-mono text-sm">{note.participant_number || note.participant_id}</TableCell>
                          <TableCell className="max-w-xs truncate" title={note.subject}>{note.subject}</TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={
                                note.category === 'Case Management' ? 'bg-orange-50 text-orange-700' :
                                note.category === 'Group Session' ? 'bg-blue-50 text-blue-700' :
                                'bg-purple-50 text-purple-700'
                              }
                            >
                              {note.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{formatDate(note.service_date)}</TableCell>
                          <TableCell className="text-sm">{note.entered_by}</TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              asChild
                            >
                              <Link to={`/cases/${note.case_id}?note=${note.note_id}`}>
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Export Buttons */}
          <div className="flex justify-center gap-4">
            <Button 
              variant="outline" 
              onClick={exportReport}
              data-testid="arp-export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button 
              onClick={() => window.print()}
              data-testid="arp-print-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          </div>

          {/* Report Footer */}
          <div className="text-center text-sm text-muted-foreground">
            <p>
              Report generated on {formatDateTime(reportData.generated_at)} by {reportData.generated_by}
            </p>
          </div>
        </>
      )}

      {/* Initial State */}
      {!reportData && !loading && (
        <Card className="card-shadow">
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Heart className="w-16 h-16 mx-auto mb-4 opacity-50 text-teal-300" />
              <h3 className="text-lg font-medium mb-2">Select a Date Range</h3>
              <p>Choose start and end dates above to generate the Addictions Recovery Program report</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participant Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {selectedParticipant?.participant_name} - Case Notes
            </DialogTitle>
            <DialogDescription>
              Qualifying case notes for {formatDate(startDate)} through {formatDate(endDate)}
            </DialogDescription>
          </DialogHeader>
          
          {loadingNotes ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : participantNotes.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Case ID</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {participantNotes.map((note) => (
                  <TableRow key={note.id}>
                    <TableCell>{formatDate(note.date_of_service || note.created_at)}</TableCell>
                    <TableCell className="font-medium">{note.subject}</TableCell>
                    <TableCell className="font-mono text-sm">{note.case_id}</TableCell>
                    <TableCell>{note.author_name || 'Unknown'}</TableCell>
                    <TableCell>
                      <Badge variant={note.status === 'approved' ? 'default' : 'secondary'}>
                        {note.status || 'N/A'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No qualifying case notes found for this participant</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddictionsRecoveryReportPage;
